import * as React from "react"
import * as Yup from "yup"
import { CmsVendor, ContentfulSpace } from "@modules/graphql/types"
import { useAvailableContentfulOrganizationsForUserQuery } from "@modules/cms/shared/queries.generated"
import {
  RadioButtonConnectedField,
  SelectConnectedField,
  Text,
} from "gatsby-interface"
import Form from "@modules/form/components/Form"
import { composeIntegrationConfig } from "../utils"
import { ConnectCmsFormProps } from "../types"
import { ConnectCmsFormControls } from "./ConnectCmsFormControls"
import { useSetCmsIntegration } from "../hooks/useSetCmsIntegration"
import { CmsDataType } from "@modules/cms/shared/utils"
import Loading from "@modules/ui/components/Loading"
import {
  ConnectCmsFormLayout,
  loadingContainerCss,
} from "./ConnectCmsFormLayout"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import {
  commonMessageTags,
  CommonTagNames,
  FormattedMessage,
} from "@modules/locales"
import { OnboardingSiteType } from "@modules/onboarding/shared/types"
import GatsbyProvisionLearnMoreMessage from "@modules/cms/shared/components/GatsbyProvisionLearnMoreMessage"
import RefreshCmsAccountDataMessage from "@modules/cms/shared/components/RefreshCmsAccountDataMessage"

type FormValues = {
  dataSourceOrg: string
  dataSourceSpace: string
}

const validationSchema = Yup.object().shape<FormValues>({
  dataSourceOrg: Yup.string().required(
    cmsIntegrationText.messages.validationContentfulOrgRequired
  ),
  dataSourceSpace: Yup.string().required(
    cmsIntegrationText.messages.validationContentfulSpaceRequired
  ),
})

export type ConnectContentfulSpaceFormProps = ConnectCmsFormProps

export function ConnectContentfulSpaceForm({
  siteId,
  siteName,
  siteType,
  onFetchError,
  onComplete,
  onCancel,
}: ConnectContentfulSpaceFormProps) {
  const {
    data,
    loading,
    error,
    refetch,
  } = useAvailableContentfulOrganizationsForUserQuery({
    fetchPolicy: `network-only`,
    variables: {
      siteId,
    },
    onError: onFetchError,
    notifyOnNetworkStatusChange: true,
  })
  const [setCmsIntegration] = useSetCmsIntegration({
    siteId,
  })

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading || data === undefined) {
    return (
      <div css={loadingContainerCss}>
        <Loading
          variant="baby"
          message={cmsIntegrationText.messages.loadingContentfulSpaces}
        />
      </div>
    )
  }

  const contentfulOrgs = data?.availableContentfulOrganizationsForUser ?? []
  type Option = { value: string; label: string }
  const orgOptions: Option[] = []
  const orgSpaceOptions: { [k: string]: Option[] } = {}
  const flatSpaces: ContentfulSpace[] = []
  let initialOrgId = contentfulOrgs[0]?.id ?? ``
  let initialSpaceId = ``

  for (const org of contentfulOrgs) {
    orgOptions.push({
      value: org.id,
      label: org.name,
    })
    orgSpaceOptions[org.id] = []
    for (const orgSpace of org.spaces ?? []) {
      flatSpaces.push(orgSpace)
      orgSpaceOptions[org.id].push({
        value: orgSpace.id,
        label: orgSpace.name,
      })
      if (orgSpace.selected) {
        initialSpaceId = orgSpace.id
        initialOrgId = org.id
      }
    }
  }

  return (
    <Form<FormValues>
      initialValues={{
        dataSourceOrg: initialOrgId,
        dataSourceSpace: initialSpaceId,
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        return setCmsIntegration({
          variables: {
            config: composeIntegrationConfig(
              CmsDataType.ContentfulSpace,
              flatSpaces,
              values.dataSourceSpace
            ),
            siteId,
            vendor: CmsVendor.Contentful,
          },
        }).finally(onComplete)
      }}
      enableReinitialize
    >
      {({ isSubmitting, values, setValues }) => {
        const spaceOptions: Option[] = [
          {
            value: ``,
            label: cmsIntegrationText.labels.contentfulSpacePlaceholder,
          },
        ].concat(orgSpaceOptions[values.dataSourceOrg] ?? [])

        return (
          <ConnectCmsFormLayout
            description={
              <FormattedMessage<"siteName", CommonTagNames>
                message={cmsIntegrationText.messages.chooseContentfulDataSource}
                values={{ siteName }}
                tags={commonMessageTags}
              />
            }
            controls={
              <ConnectCmsFormControls
                isSubmitting={isSubmitting}
                onCancel={onCancel}
              />
            }
            refreshMessage={
              <Text size="S">
                <RefreshCmsAccountDataMessage
                  message={
                    cmsIntegrationText.messages.refetchContentfulAccountData
                  }
                  refetch={refetch}
                />
              </Text>
            }
            instructions={
              siteType === OnboardingSiteType.DeployNowProvision ? (
                <>
                  <Text size="S">
                    <FormattedMessage<"cmsProjectName", CommonTagNames>
                      message={
                        cmsIntegrationText.messages
                          .deployNowProvisionSelectEmptyDataSource
                      }
                      values={{
                        cmsProjectName: CmsDataType.ContentfulSpace,
                      }}
                      tags={commonMessageTags}
                    />
                  </Text>
                  <Text size="S">
                    <GatsbyProvisionLearnMoreMessage
                      cmsVendor={CmsVendor.Contentful}
                    />
                  </Text>
                </>
              ) : null
            }
          >
            {orgOptions.length > 1 && (
              <RadioButtonConnectedField
                name="dataSourceOrg"
                label={cmsIntegrationText.labels.contentfulOrg}
                options={orgOptions}
                variant="framed"
                onChange={e => {
                  setValues(
                    {
                      dataSourceOrg: e.target.value,
                      dataSourceSpace: ``,
                    },
                    false
                  )
                }}
              />
            )}
            <SelectConnectedField
              name="dataSourceSpace"
              label={cmsIntegrationText.labels.contentfulSpace}
              options={spaceOptions}
            />
          </ConnectCmsFormLayout>
        )
      }}
    </Form>
  )
}
